import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const ToasterContext = createContext();

export const ToasterProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");
  const lastToastRef = useRef({ message: null, type: null, timestamp: 0 });

  const show = useCallback((msg, toastType = "info", options = {}) => {
    // Deduplication: Only show if not the same as last toast within 2s
    const now = Date.now();
    if (
      lastToastRef.current.message === msg &&
      lastToastRef.current.type === toastType &&
      now - lastToastRef.current.timestamp < 2000
    ) {
      return;
    }
    lastToastRef.current = { message: msg, type: toastType, timestamp: now };
    setMessage(msg);
    setType(toastType);
    setOpen(false); // Close any open toast first
    setTimeout(() => setOpen(true), 50); // Open new toast after short delay
  }, []);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  // Expose show method globally
  Toaster.show = show;

  // Map severity to icon color
  const iconColors = {
    success: "#43a047",
    error: "#d32f2f",
    warning: "#ed6c02",
    info: "#0288d1",
  };

  return (
    <ToasterContext.Provider value={{ show }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={2000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        sx={{
          // Ensure toast stays above modals/overlays such as OTP blur screens (OTP popup uses zIndex: 2000)
          zIndex: 9999,
        }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={handleClose}
          severity={type}
          sx={{
            minWidth: 240,
            fontWeight: 500,
            fontSize: "1rem",
            backgroundColor: "#fff",
            color: "#222",
            boxShadow: 3,
            border: "1px solid #eee",
            alignItems: "center",
            "& .MuiAlert-icon": {
              color: (theme) => iconColors[type] || "#0288d1",
            },
          }}
        >
          {message}
        </MuiAlert>
      </Snackbar>
    </ToasterContext.Provider>
  );
};

// For convenience, allow useToaster hook
export const useToaster = () => useContext(ToasterContext);

// Default export for backward compatibility (render ToasterProvider in App.js)
const Toaster = () => null;
Toaster.show = () => {};
export default Toaster;
